import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  model,
  Output,
  ViewChild,
} from '@angular/core';
import { TreeTable, TreeTableModule } from 'primeng/treetable';
import { NgClass, NgStyle } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import {
  TreeTableColumnReorderEvent,
} from 'primeng/treetable/treetable.interface';
import { SkeletonModule } from 'primeng/skeleton';
import { TableHeaderColumn } from '@everymatrix-base-components/utils';
import { PopoverModule } from 'primeng/popover';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'lib-dm-tree-table',
  imports: [
    TreeTableModule,
    NgStyle,
    TooltipModule,
    NgClass,
    SkeletonModule,
    PopoverModule,
    CheckboxModule,
    FormsModule,
  ],
  templateUrl: './dm-tree-table.component.html',
  styleUrl: './dm-tree-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class DmTreeTableComponent {
  @ViewChild('treeTable', { static: false }) treeTable!: TreeTable;

  @Output()
  loadMoreEmitter: EventEmitter<number> = new EventEmitter();
  @Output()
  sortingEmitter: EventEmitter<{
    field: string | null;
    value: 'asc' | 'desc' | null;
  }> = new EventEmitter();
  @Input()
  set rawTableData(value: any) {
    if (value && value.length) {
      this.mapRawTableData(value);
    } else if (value && !value.length) {
      this.tableData = value;
    }
  }
  @Input()
  set startIndex(data: number) {
    if (data === 0 && this.offset !== undefined) {
      this.treeTable.reset();
    }
    this.offset = data;
  }
  @Input()
  tableColumns!: TableHeaderColumn[];
  @Input()
  rowsPerPageOptions = [10, 20, 30, 50, 100];
  @Input()
  generalTotal!: string;
  limit = model(100);
  @Input()
  scrollHeight = 350;
  @Input()
  sorting = true;
  @Input()
  rowHover = true;
  @Input()
  reorderableColumns = true;
  @Input()
  resizableColumns = true;
  @Input()
  paginator = true;
  @Input()
  loading!: boolean;
  frozenWidth = 0;
  offset!: number;

  constructor(private cdr: ChangeDetectorRef) {}

  placeholderData = Array(9)
    .fill(null)
    .map(() => ({
      data: {},
      children: [],
    }));
  tableData!: any[];
  frozenColumns: TableHeaderColumn[] = [];

  private mapRawTableData(value: any[]) {
    const result = value.map((item) => ({
      data: item,
      children: [],
    }));

    this.tableData = [...result];
    this.cdr.detectChanges();
  }

  protected changeSorting(col: TableHeaderColumn, index: number): void {
    this.tableColumns.forEach((c, i) => {
      if (i !== index) {
        c.sortOrder = 0;
      }
    });

    this.offset = 0;
    this.treeTable.reset();

    if (col.sortOrder === 0 || !col.sortOrder) {
      col.sortOrder = 1;
    } else if (col.sortOrder === 1) {
      col.sortOrder = -1;
    } else {
      col.sortOrder = 0;
    }

    if (col.sortOrder !== 0) {
      this.sortingEmitter.emit({
        field: col.field,
        value: col.sortOrder === 1 ? 'asc' : 'desc',
      });
    } else {
      this.sortingEmitter.emit({
        field: null,
        value: null,
      });
    }
  }

  protected onColReorder(data: TreeTableColumnReorderEvent): void {
    const { dragIndex, dropIndex } = data;

    if (dragIndex && dropIndex) {
      const [draggedColumn] = this.tableColumns.splice(dragIndex, 1);

      let adjustedDropIndex = dropIndex;
      if (dragIndex < dropIndex) {
        adjustedDropIndex = dropIndex - 1;
      }

      this.tableColumns.splice(adjustedDropIndex, 0, draggedColumn);

      this.tableColumns.forEach((col, index) => {
        col.orderNumber = index;
      });
    }
  }

  protected toggleFrozenColumn(col: TableHeaderColumn): void {
    const frozenIndex = this.frozenColumns.findIndex(
      (fc) => fc.field === col.field
    );

    if (frozenIndex >= 0) {
      col.isPinned = false;
      this.frozenColumns.splice(frozenIndex, 1);
      this.tableColumns.splice(col.orderNumber, 0, col);
      this.frozenWidth = +this.frozenWidth - col.minWidth;
    } else {
      col.isPinned = true;
      this.frozenColumns.push(col);
      this.tableColumns = this.tableColumns.filter(
        (c) => c.field !== col.field
      );
      this.frozenWidth = +this.frozenWidth + col.minWidth;
    }

    if (!this.frozenColumns.length) {
      this.frozenWidth = 0;
    }
  }

  protected onPageChange(event: any) {
    if (event.rows !== this.limit()) {
      const pageIndex = Math.floor(this.offset / this.limit());
      this.offset= pageIndex * event.rows;
      this.limit.set(event.rows as number);
    } else {
      this.offset = event.first;
    }
    this.loadMoreEmitter.emit(this.offset);
  }

  get visibleColumns(): TableHeaderColumn[] {
    return this.tableColumns.filter((col) => col.visible);
  }
}
